import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TaskService } from '@app/_components/task/task.service';
import { TaskLineItemService } from '@app/_components/task-line-item/task-line-item.service';
import { TaskRequirementService } from '@app/_components/task-requirement/task-requirement.service';
import { OrderProductService } from '@app/_components/order-product/order-product.service';
import { TaskLineItem } from '@app/_components/task-line-item/task-line-item';
import { TaskRequirement } from '@app/_components/task-requirement/task-requirement';
import { getDomainOptions, TaskCodeRegistry } from '@app/_constants/task-code-registry';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-task-template-edit',
  templateUrl: './template-edit.component.html',
  styleUrls: ['./template-edit.component.scss'],
  providers: [MessageService],
  standalone: false
})
export class TaskTemplateEditComponent implements OnInit {

  form: FormGroup;
  taskId: number | null = null;
  loading = false;
  saving = false;
  isNew = false;

  // options
  priorityOptions = getDomainOptions('PRIORITY');
  regionOptions = getDomainOptions('REGION');
  billableOptions = getDomainOptions('BILLABLE');
  lineTypeOptions = getDomainOptions('LINE_TYPE');
  supplySourceOptions = getDomainOptions('SUPPLY_SOURCE');

  triggerOptions = [
    { value: TaskCodeRegistry.TRIGGER_RESERVATION_CHECKOUT,        label: 'Reservation Checkout' },
    { value: TaskCodeRegistry.TRIGGER_RESERVATION_CHECKIN_T_MINUS, label: 'Reservation Check-in T-minus' },
    { value: TaskCodeRegistry.TRIGGER_OCCUPANCY_THRESHOLD,         label: 'Occupancy Threshold' },
    { value: TaskCodeRegistry.TRIGGER_GUEST_PORTAL_REQUEST,        label: 'Guest Portal Request' },
    { value: TaskCodeRegistry.TRIGGER_INSPECTION_FAILED,           label: 'Inspection Failed' },
    { value: TaskCodeRegistry.TRIGGER_MANUAL,                      label: 'Manual' },
    { value: TaskCodeRegistry.TRIGGER_RECURRING_CALENDAR,          label: 'Recurring Calendar' },
    { value: TaskCodeRegistry.TRIGGER_TASK_CHAIN,                  label: 'Task Chain' },
  ];

  // vendor / team assignment
  vendors: any[] = [];
  teams: any[] = [];

  // product autocomplete
  productSuggestions: any[] = [];
  allProducts: any[] = [];

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private taskService: TaskService,
    private taskLineItemService: TaskLineItemService,
    private taskRequirementService: TaskRequirementService,
    private orderProductService: OrderProductService,
    private route: ActivatedRoute,
    public router: Router,
    private messageService: MessageService
  ) {}

  isClone = false;

  ngOnInit(): void {
    this.buildForm();
    this.loadProducts();
    this.loadVendorsAndTeams();

    const id = this.route.snapshot.paramMap.get('id');
    const pathSegments = this.route.snapshot.url.map(s => s.path);
    const cloneMode = pathSegments.includes('clone');

    if (id && id !== 'new') {
      if (cloneMode) {
        this.isClone = true;
        this.isNew = true;
        this.loadTemplate(Number(id));
      } else {
        this.taskId = Number(id);
        this.loadTemplate(this.taskId);
      }
    } else {
      this.isNew = true;
    }
  }

  buildForm(): void {
    this.form = this.fb.group({
      // Header
      name: ['', Validators.required],
      description: [''],
      hoursEstToComplete: [null],
      priorityTier: [TaskCodeRegistry.PRIORITY_P2_NORMAL],
      region: [TaskCodeRegistry.REGION_BOTH],
      billableEntity: [TaskCodeRegistry.BILLABLE_OWNER],
      requiresEmptyUnit: [false],
      // Trigger (int_1, int_2, int_3, str_1 slots)
      triggerType: [TaskCodeRegistry.TRIGGER_MANUAL],
      triggerParam1: [null],
      triggerParam2: [null],
      triggerParamString: [''],
      // Metadata
      isTemplate: [true],
      // Assignment defaults
      assignedToTeamId: [null],
      assignedToVendorId: [null],
      // Line items inline table
      lineItems: this.fb.array([]),
      // Requirements inline table
      requirements: this.fb.array([])
    });
  }

  loadProducts(): void {
    this.orderProductService.findAllOrderProducts().subscribe({
      next: (res) => {
        if (res && res.data) {
          this.allProducts = res.data.orderProducts || [];
        }
      },
      error: () => {}
    });
  }

  loadVendorsAndTeams(): void {
    forkJoin({
      vendors: this.http.get<any>(`${environment.apiUrl}/vendors?isActive=true`).pipe(catchError(() => of(null))),
      teams:   this.http.get<any>(`${environment.apiUrl}/task-teams`).pipe(catchError(() => of(null)))
    }).subscribe({
      next: ({ vendors, teams }) => {
        this.vendors = vendors?.data?.vendors || vendors?.data || [];
        this.teams   = teams?.data?.taskTeams || teams?.data || [];
      },
      error: () => {}
    });
  }

  filteredVendors(): any[] {
    const regionVal = this.form.get('region')?.value;
    if (!regionVal) { return this.vendors; }
    return this.vendors.filter(v => !v.region || v.region === regionVal);
  }

  loadTemplate(id: number): void {
    this.loading = true;
    forkJoin({
      task: this.taskService.findById(id).pipe(catchError(() => of(null))),
      lineItems: this.taskLineItemService.findByTaskId(id).pipe(catchError(() => of(null))),
      requirements: this.taskRequirementService.findByTaskId(id).pipe(catchError(() => of(null)))
    }).subscribe({
      next: ({ task, lineItems, requirements }) => {
        if (task && task.data) {
          const t = task.data.task;
          this.form.patchValue({
            name: this.isClone ? (t.name + ' (Copy)') : t.name,
            description: t.miniDoc,
            hoursEstToComplete: t.hoursEstToComplete,
            priorityTier: t.priorityTier || TaskCodeRegistry.PRIORITY_P2_NORMAL,
            region: t.region || TaskCodeRegistry.REGION_BOTH,
            billableEntity: t.billableEntity || TaskCodeRegistry.BILLABLE_OWNER,
            requiresEmptyUnit: t.requiresEmptyUnit || false,
            triggerType: t.int1 || TaskCodeRegistry.TRIGGER_MANUAL,
            triggerParam1: t.int2,
            triggerParam2: t.int3,
            triggerParamString: t.str1 || '',
            isTemplate: true,
            assignedToTeamId: t.assignedToTeamId || null,
            assignedToVendorId: t.assignedToVendorId || null
          });
          if (this.isClone) {
            // Clones save as a new record — no taskId
            this.taskId = null;
          }
        }
        if (lineItems && lineItems.data) {
          const items: TaskLineItem[] = lineItems.data.taskLineItems || [];
          items.filter(i => !i.isDeleted).forEach(item => this.addLineItemRow(item));
        }
        if (requirements && requirements.data) {
          const reqs: TaskRequirement[] = requirements.data.taskRequirements || [];
          reqs.filter(r => !r.isDeleted).forEach(req => this.addRequirementRow(req));
        }
        this.loading = false;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load template' });
        this.loading = false;
      }
    });
  }

  // --- LINE ITEMS ---

  get lineItemsArray(): FormArray {
    return this.form.get('lineItems') as FormArray;
  }

  addLineItemRow(item?: Partial<TaskLineItem>): void {
    this.lineItemsArray.push(this.fb.group({
      taskLineItemId: [item?.taskLineItemId || null],
      lineType: [item?.lineType || TaskCodeRegistry.LINE_TYPE_LABOR],
      orderProductId: [item?.orderProductId || null],
      orderProductName: [item?.orderProductName || ''],
      description: [item?.description || ''],
      qty: [item?.qty != null ? item.qty : 1, [Validators.required, Validators.min(0)]],
      unitCost: [item?.unitCost != null ? item.unitCost : 0, [Validators.required, Validators.min(0)]],
      billableToOwner: [item?.billableToOwner != null ? item.billableToOwner : true],
      supplySource: [item?.supplySource || null],
      isRequired: [item?.isRequired != null ? item.isRequired : true],
      ordinal: [item?.ordinal || 0]
    }));
  }

  removeLineItem(index: number): void {
    this.lineItemsArray.removeAt(index);
  }

  getLineItemTotal(index: number): number {
    const row = this.lineItemsArray.at(index);
    const qty = row.get('qty').value || 0;
    const cost = row.get('unitCost').value || 0;
    return qty * cost;
  }

  get lineItemsRunningTotal(): number {
    return this.lineItemsArray.controls.reduce((sum, ctrl) => {
      return sum + ((ctrl.get('qty').value || 0) * (ctrl.get('unitCost').value || 0));
    }, 0);
  }

  searchProducts(event: any): void {
    const q = (event.query || '').toLowerCase();
    this.productSuggestions = this.allProducts
      .filter(p => (p.name || '').toLowerCase().includes(q))
      .slice(0, 30);
  }

  onProductSelect(product: any, index: number): void {
    const row = this.lineItemsArray.at(index);
    row.patchValue({
      orderProductId: product.orderProductId,
      orderProductName: product.name,
      unitCost: product.listPrice || 0
    });
  }

  // --- REQUIREMENTS ---

  get requirementsArray(): FormArray {
    return this.form.get('requirements') as FormArray;
  }

  addRequirementRow(req?: Partial<TaskRequirement>): void {
    this.requirementsArray.push(this.fb.group({
      taskRequirementId: [req?.taskRequirementId || null],
      name: [req?.name || '', Validators.required],
      category: [req?.category || ''],
      description: [req?.description || ''],
      requiresPhoto: [req?.requiresPhoto || false],
      requiresNote: [req?.requiresNote || false],
      isRequired: [req?.isRequired != null ? req.isRequired : true],
      ordinal: [req?.ordinal || 0]
    }));
  }

  removeRequirement(index: number): void {
    this.requirementsArray.removeAt(index);
  }

  // --- SAVE ---

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    const formVal = this.form.value;

    const taskPayload: any = {
      taskId: this.taskId,
      name: formVal.name,
      miniDoc: formVal.description,
      hoursEstToComplete: formVal.hoursEstToComplete,
      priorityTier: formVal.priorityTier,
      region: formVal.region,
      billableEntity: formVal.billableEntity,
      requiresEmptyUnit: formVal.requiresEmptyUnit,
      int1: formVal.triggerType,
      int2: formVal.triggerParam1,
      int3: formVal.triggerParam2,
      str1: formVal.triggerParamString,
      isTemplate: true,
      assignedToTeamId: formVal.assignedToTeamId || null,
      assignedToVendorId: formVal.assignedToVendorId || null
    };

    const saveTask$ = this.taskId
      ? this.taskService.update(taskPayload)
      : this.taskService.add(taskPayload);

    saveTask$.subscribe({
      next: (res) => {
        const savedTaskId = (res.data && res.data.taskId) ? res.data.taskId : this.taskId;

        if (!savedTaskId) {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Task save did not return a taskId' });
          this.saving = false;
          return;
        }

        const lineItems: TaskLineItem[] = formVal.lineItems.map((li: any, i: number) => ({
          ...li,
          taskId: savedTaskId,
          ordinal: i
        }));

        const requirements: TaskRequirement[] = formVal.requirements.map((r: any, i: number) => ({
          ...r,
          taskId: savedTaskId,
          ordinal: i
        }));

        const saves = [];
        if (lineItems.length) {
          saves.push(this.taskLineItemService.saveAll(savedTaskId, lineItems).pipe(catchError(() => of(null))));
        }
        if (requirements.length) {
          saves.push(this.taskRequirementService.saveAll(savedTaskId, requirements).pipe(catchError(() => of(null))));
        }

        if (saves.length === 0) {
          this.onSaveComplete(savedTaskId);
          return;
        }

        forkJoin(saves).subscribe({
          next: () => this.onSaveComplete(savedTaskId),
          error: () => {
            this.messageService.add({ severity: 'warn', summary: 'Partial Save', detail: 'Template saved but line items/requirements may have failed' });
            this.saving = false;
          }
        });
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save template' });
        this.saving = false;
      }
    });
  }

  onSaveComplete(savedTaskId: number): void {
    this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Template saved successfully' });
    this.saving = false;
    if (this.isNew) {
      this.router.navigate(['/task-templates', savedTaskId]);
    }
  }

  onCancel(): void {
    this.router.navigate(['/task-templates']);
  }

  isFieldInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return ctrl && ctrl.invalid && ctrl.touched;
  }
}
